// The four steps of the LinkedIn bonus as one scene that looks like LinkedIn itself (Robert, 29.10.2026: "like the
// Wix uninstall guide", and "it has to look like LinkedIn, everybody knows it"): the composer with the post already in
// it, where the mouse clicks "Post"; the post in the feed, where the comment with the link is pasted and sent; the Ultimo Bots credits
// card, into which "+1,000" flies while the number counts up; then the customers whose payments travel as coins into
// your wallet (the commission). One 14 s loop, the step list below follows it, and a click on a step jumps the loop
// there. The scene is drawn at a fixed size; its box takes its height from the width in CSS, and the scale only
// transforms the drawing, so a change of scale never changes the layout (it used to, and the scene trembled).
// Without motion it shows the post, the comment and the credits.
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faBriefcase, faBuilding, faCalendar, faCheck, faCommentDots, faEarthAmericas, faEllipsis, faHandsClapping, faHeart, faHouse,
  faImage, faMagnifyingGlass, faPaperPlane, faRetweet, faShop, faStore, faThumbsUp, faUserGroup, faWallet, faXmark } from '@fortawesome/free-solid-svg-icons';
import { LINKEDIN_TEMPLATES } from '../constants/linkedinBonusTemplates';
import './LinkedInBonusSteps.css';

const W = 560;
const LOOP_MS = 14000;
// Where each step starts in the loop; the change of picture happens just before, so a jump lands on a clean picture.
const STEP_AT = [0, 0.25, 0.5, 0.75];
const CUSTOMERS = [faStore, faShop, faBuilding];
const COINS = [0, 1, 2, 3, 4, 5];
const CURSOR = 'M0 0v17l4.6-4.2 3 6.8 2.8-1.2-3-6.8h6z';

// A letter, a digit or "in", exactly in the middle of its badge (Robert, 29.10.2026: "not perfectly centred"). HTML
// centres the line box, and every font sits its glyphs a little off that box's middle; here the glyph's own ink is
// measured in the font it renders with and centred in an SVG. Until then, or without canvas metrics, the baseline
// sits half a cap height below the middle. `size` is the font size as a share of the badge.
export function Glyph({ text, size }) {
  const ref = useRef(null);
  const [ink, setInk] = useState([0, 0.35]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !document.fonts) return undefined;
    let alive = true;
    const { fontWeight, fontFamily } = getComputedStyle(el);
    const font = `${fontWeight} 100px ${fontFamily}`;
    const measure = () => {
      const ctx = alive && document.createElement('canvas').getContext('2d');
      if (!ctx) return;
      ctx.font = font;
      ctx.textAlign = 'center';
      const m = ctx.measureText(text);
      if (ctx.font.includes('100px') && m.actualBoundingBoxAscent) {
        setInk([(m.actualBoundingBoxLeft - m.actualBoundingBoxRight) / 200, (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 200]);
      }
    };
    document.fonts.load(font, text).then(measure, measure);
    return () => { alive = false; };
  }, [text]);
  const em = size * 100;
  return <svg className="lbs-glyph" viewBox="-50 -50 100 100" aria-hidden="true">
    <text ref={ref} x={ink[0] * em} y={ink[1] * em} fontSize={em} textAnchor="middle">{text}</text>
  </svg>;
}

function Avatar({ name, small }) {
  return <span className={`lbs-av${small ? ' is-small' : ''}`}><Glyph text={(name[0] || 'U').toUpperCase()} size={small ? 0.44 : 0.41} /></span>;
}

// Payments of your customers travel as coins into your wallet.
function MoneyFlow({ rate }) {
  const { t } = useTranslation();
  return <div className="lb-flow">
    <div className="lb-flow-side">
      <div className="lb-custs">{CUSTOMERS.map((icon, i) => <span key={i} className="lb-cust" style={{ animationDelay: `${i * 0.5}s` }}>
        <span className="lb-cust-icon"><FontAwesomeIcon icon={icon} /></span><span className="lb-cust-lines"><i /><i /></span>
      </span>)}</div>
      <span className="lb-flow-label">{t('bonus_flow_customers')}</span>
    </div>
    <div className="lb-track">
      {COINS.map((i) => <span key={i} className="lb-coin" style={{ animationDelay: `${i * 0.5}s`, '--row': (i % 3) - 1 }}>$</span>)}
    </div>
    <div className="lb-flow-side">
      <div className="lb-wallet"><FontAwesomeIcon icon={faWallet} /><span className="lb-wallet-rate">+{rate}</span></div>
      <span className="lb-flow-label">{t('bonus_flow_you')}</span>
    </div>
  </div>;
}

export default function LinkedInBonusSteps({ credits, amount, rate, link }) {
  const { t, i18n } = useTranslation();
  const root = useRef(null);
  const frame = useRef(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = frame.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    // Only a real change of width counts; the scale moves nothing in the layout.
    const ro = new ResizeObserver(() => setScale((s) => { const next = el.clientWidth / W || 1; return Math.abs(next - s) > 0.002 ? next : s; }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A click on a step jumps every part of the loop (the step list too) to where that step starts.
  const jump = (i) => {
    (root.current?.getAnimations?.({ subtree: true }) || []).forEach((a) => {
      if (a.effect?.getTiming?.().duration === LOOP_MS) a.currentTime = STEP_AT[i] * LOOP_MS;
    });
  };

  const lang = (i18n.language || 'en').slice(0, 2);
  const template = LINKEDIN_TEMPLATES[lang] || LINKEDIN_TEMPLATES.en;
  const opening = template.post.split('\n\n').slice(0, 2).join('\n\n');
  const lead = template.comment('').trim();
  const shown = (link || 'https://portal.ultimo-bots.com/login?ref=…').replace(/^https?:\/\//, '');
  const name = (localStorage.getItem('first_name') || '').trim() || t('bonus_li_you');
  const steps = [t('bonus_s3_1'), t('bonus_s3_2'), t('bonus_gift', { credits }), t('bonus_s3_4', { rate })];

  return <div className="lbs" ref={root}>
    <div className="lbs-frame" ref={frame} aria-hidden="true">
      <div className="lbs-stage" style={{ transform: `scale(${scale})`, '--lbs-to': amount }}>
        <div className="lbs-nav">
          <span className="lbs-logo"><Glyph text="in" size={0.65} /></span>
          <span className="lbs-search"><FontAwesomeIcon icon={faMagnifyingGlass} />{t('bonus_li_search')}</span>
          <span className="lbs-spacer" />
          {[faHouse, faUserGroup, faBriefcase, faCommentDots, faBell].map((icon, i) => <FontAwesomeIcon key={i} icon={icon} className="lbs-nav-icon" />)}
        </div>

        {/* 1 — the composer: the post is there, the mouse clicks "Post" */}
        <div className="lbs-composer">
          <div className="lbs-head">
            <Avatar name={name} />
            <span className="lbs-who"><b>{name}</b><span className="lbs-anyone"><FontAwesomeIcon icon={faEarthAmericas} /> {t('bonus_li_anyone')}</span></span>
            <FontAwesomeIcon icon={faXmark} className="lbs-close" />
          </div>
          <p className="lbs-c-text">{opening}</p>
          <div className="lbs-c-foot">
            <FontAwesomeIcon icon={faImage} /><FontAwesomeIcon icon={faCalendar} /><FontAwesomeIcon icon={faEllipsis} />
            <span className="lbs-spacer" />
            <span className="lbs-c-post">{t('bonus_li_post')}</span>
          </div>
        </div>

        {/* 2 — the post in the feed: reactions, the first comment with the link */}
        <div className="lbs-post">
          <div className="lbs-head">
            <Avatar name={name} />
            <span className="lbs-who"><b>{name}</b><span className="lbs-meta">{t('bonus_li_now')} • <FontAwesomeIcon icon={faEarthAmericas} /></span></span>
            <FontAwesomeIcon icon={faEllipsis} className="lbs-close" />
          </div>
          <p className="lbs-p-text">{opening.replace(/\n\n/g, '\n')}<span className="lbs-more">{t('bonus_li_more')}</span></p>
          <div className="lbs-reacts">
            <span className="lbs-r is-like"><FontAwesomeIcon icon={faThumbsUp} /></span>
            <span className="lbs-r is-love"><FontAwesomeIcon icon={faHeart} /></span>
            <span className="lbs-r is-clap"><FontAwesomeIcon icon={faHandsClapping} /></span>
            <span className="lbs-r-count">12</span>
          </div>
          <div className="lbs-actions">
            <span><FontAwesomeIcon icon={faThumbsUp} />{t('bonus_li_like')}</span>
            <span><FontAwesomeIcon icon={faCommentDots} />{t('bonus_li_comment')}</span>
            <span><FontAwesomeIcon icon={faRetweet} />{t('bonus_li_repost')}</span>
            <span><FontAwesomeIcon icon={faPaperPlane} />{t('bonus_li_send')}</span>
          </div>
          <div className="lbs-cz">
            <div className="lbs-cz-write">
              <Avatar name={name} small />
              <span className="lbs-input">
                <span className="lbs-ph">{t('bonus_li_add_comment')}</span>
                <span className="lbs-in-text">{lead} <span className="lbs-link">{shown}</span></span>
              </span>
              <span className="lbs-c-btn">{t('bonus_li_comment')}</span>
            </div>
            <div className="lbs-cz-done">
              <Avatar name={name} small />
              <span className="lbs-bubble"><b>{name}</b>{lead} <span className="lbs-link">{shown}</span></span>
            </div>
          </div>
        </div>

        {/* 3 — the credits arrive in Ultimo Bots */}
        <div className="lbs-acct">
          <span className="lbs-acct-head"><span className="lbs-u"><Glyph text="U" size={0.6} /></span>Ultimo Bots</span>
          <span className="lbs-acct-label">{t('bonus_li_credits')}</span>
          {/* Counts up plainly, then shows the amount as the language writes it (1,000 / 1.000) */}
          <span className="lbs-count"><span className="lbs-count-run" /><span className="lbs-count-end">+{credits}</span></span>
          <span className="lbs-bar"><span className="lbs-fill" /></span>
          <span className="lbs-check"><FontAwesomeIcon icon={faCheck} /></span>
        </div>
        <span className="lbs-fly">+{credits}</span>

        {/* 4 — the customers pay, the commission flies to you */}
        <div className="lbs-earn">
          <p className="lbs-earn-title">{t('bonus_earn_title', { rate })}</p>
          <MoneyFlow rate={rate} />
        </div>

        <span className="lbs-ring" />
        <svg className="lbs-cursor" viewBox="0 0 16 22" width="16" height="22"><path d={CURSOR} fill="#fff" stroke="#111" strokeWidth="1.3" strokeLinejoin="round" /></svg>
      </div>
    </div>
    <ol className="lbs-steps">
      {steps.map((text, i) => <li key={i}>
        <button type="button" className={`lbs-step lbs-step-${i + 1}`} onClick={() => jump(i)}>
          <span className="lbs-num"><Glyph text={String(i + 1)} size={0.55} /></span>
          <span className="lbs-step-text">{text}</span>
        </button>
      </li>)}
    </ol>
  </div>;
}
